import { EmojiEvents as TrophyIcon } from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { Award } from "../../store/services/salesManagerService";

// Emoji per stabil award-key. Faller tillbaka på pokal om en ny key dyker upp.
const AWARD_EMOJI: Record<string, string> = {
  bulldog: "🐕",
  fast: "⚡",
  marathon: "🏃",
  target: "🎯",
  world: "🌍",
};

interface AwardsListProps {
  awards: Award[];
}

const AwardsList: React.FC<AwardsListProps> = ({ awards }) => {
  const theme = useTheme();

  if (!awards || awards.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Inga utmärkelser att visa.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {awards.map((award) => {
        const hasWinner = award.SalespersonId != null;
        const accent = hasWinner
          ? theme.palette.warning.main
          : theme.palette.text.disabled;
        const emoji = AWARD_EMOJI[award.Key];

        return (
          <Box
            key={award.Key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(accent, hasWinner ? 0.08 : 0.04),
              border: `1px solid ${alpha(accent, hasWinner ? 0.18 : 0.1)}`,
              opacity: hasWinner ? 1 : 0.7,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: emoji ? 16 : 14,
                bgcolor: alpha(accent, 0.15),
                color: accent,
              }}
            >
              {emoji ?? <TrophyIcon sx={{ fontSize: 16 }} />}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ lineHeight: 1.2 }}
                noWrap
              >
                {award.Title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.2 }}
                noWrap
              >
                {hasWinner ? award.SalespersonName : award.Description}
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
              {hasWinner ? (
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color: theme.palette.warning.dark, lineHeight: 1.2 }}
                >
                  {award.ValueLabel}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontStyle: "italic" }}
                >
                  Ingen vinnare än
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default AwardsList;
